import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError, AxiosInstance } from 'axios';
import { fetchSendCode } from './service';
import { UseSendWithdrawalCodeProps } from './props';
import { GenericError } from '@starsoft/common/models';
import { FetchSendValidationCodeRequestDto } from '@starsoft/common/dto/withdrawals/fetch-send-validation-code-request.dto';
import { useAxiosInstance } from '@starsoft/common/hooks';

/**
 * Custom hook to handle sending withdrawal validation codes
 *
 * This hook manages the mutation state for sending validation codes for withdrawals,
 * using React Query's mutation capabilities. It handles the API call and provides
 * customizable success/error callbacks.
 *
 * @param {UseSendWithdrawalCodeProps} options - Optional configuration object
 * @param {Function} [options.customOnError] - Custom error callback function
 * @param {Function} [options.customOnSuccess] - Custom success callback function
 *
 * @returns {Object} An object containing:
 *   - sendWithdrawalCode: Function to trigger sending the validation code
 *     @param {FetchSendValidationCodeRequestDto} data - The validation code request data
 *   - isLoading: Boolean indicating if code sending is in progress
 *   - isSuccess: Boolean indicating if code was sent successfully
 */
export function useSendWithdrawalCode(options?: UseSendWithdrawalCodeProps) {
  const { customOnError, customOnSuccess } = options ?? {};
  const queryClient = useQueryClient();
  const axiosInstance: AxiosInstance = useAxiosInstance();

  /**
   * Error handler that runs both default and custom error callbacks
   * @param {AxiosError<GenericError>} error - The error that occurred
   * @param {unknown} variables - The variables passed to the mutation
   * @param {unknown} context - The mutation context
   */
  function onError(
    error: AxiosError<GenericError>,
    variables: unknown,
    context: unknown,
  ) {
    queryClient
      ?.getDefaultOptions()
      .mutations?.onError?.(error, variables, context);
    customOnError?.(error);
  }

  /**
   * Success handler that runs the custom success callback
   */
  function onSuccess() {
    customOnSuccess?.();
  }

  const {
    mutate,
    isPending: isLoading,
    isSuccess,
  } = useMutation({
    mutationFn: (data: FetchSendValidationCodeRequestDto) =>
      fetchSendCode(data, axiosInstance),
    onError,
    onSuccess,
  });

  return {
    sendWithdrawalCode: mutate,
    isLoading,
    isSuccess,
  };
}
